import {
  createContext,
  useContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

import type {
  TradeDirection,
  TransactionModuleVariant,
} from '@/components/TransactionModule/types';

import type { TokenBase, TokenResult } from '@/utils/0x/token-registry.types';
import type { ChainAddress } from '@/utils/models';

/** Module props, resolved tokens, and ephemeral rewrite view state. */
export type ModuleContextValue = {
  variant: TransactionModuleVariant;
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  isSellTokenLoading: boolean;
  isBuyTokenLoading: boolean;
  accountAddress: ChainAddress | undefined;
  onSelectToken: (token: TokenBase, direction: 'buy' | 'sell') => void;
  onSelectPair: (buyToken: TokenBase, sellToken: TokenBase) => void;
  onFlipSelectedTokens: () => void;
  onOpenTokenPanel?: (direction: TradeDirection) => void;
  autoBannerVisible: boolean;
  setAutoBannerVisible: Dispatch<SetStateAction<boolean>>;
  setSettingsOpen: (open: boolean) => void;
};

const ModuleContext = createContext<ModuleContextValue | undefined>(undefined);

type ModuleProviderProps = {
  children: ReactNode;
  value: ModuleContextValue;
};

/** Provides module props and ephemeral rewrite view state. */
export function ModuleProvider({ children, value }: ModuleProviderProps) {
  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>;
}

/** Reads the nearest rewrite module context. */
export function useModuleContext(): ModuleContextValue {
  const value = useContext(ModuleContext);
  if (!value) throw new Error('useModuleContext must be used within ModuleProvider.');
  return value;
}
